import type {
  ContractTypeDTO,
  CreateContractTypeFormDTO,
  UpdateContractTypeFormDTO,
} from "@/modules/contracts/domain/contract-types";
import type {
  CreateContractFormDTO,
  RenewContractFormDTO,
  UpdateContractFormDTO,
} from "@/modules/contracts/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { toISOForAPI } from "@/shared/lib/date-time";

const baseUrl = `${config.apiUrl}/contracts`;

export const contractsApi = {
  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch contracts: ${res.statusText}`);
    return res.json();
  },

  async get(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch contract: ${res.statusText}`);
    return res.json();
  },

  async getExpiring(
    query: Partial<
      OffsetPageQueryDTO & { days?: number; excludeDays?: number }
    > = {},
  ) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));
    if (query.days !== undefined) params.set("days", String(query.days));
    if (query.excludeDays !== undefined)
      params.set("excludeDays", String(query.excludeDays));

    const url = `${baseUrl}/expiring?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch expiring contracts: ${res.statusText}`);
    return res.json();
  },

  async create(
    input: CreateContractFormDTO & {
      documents?: Array<{
        id: string;
        title: string;
        fileKey: string | null;
        documentType: "main" | "attachment";
        notes?: string | null;
      }>;
    },
  ) {
    const formData = new FormData();

    if (input.contractNumber)
      formData.set("contractNumber", input.contractNumber);
    formData.set("title", input.title);
    formData.set("typeId", input.typeId);
    formData.set("status", input.status);
    formData.set("counterparty", input.counterparty);
    if (input.counterpartyContact)
      formData.set("counterpartyContact", input.counterpartyContact);

    // Handle nullable fields - only send if they have a value (don't send empty strings)
    if (input.customerId && input.customerId !== "") {
      formData.set("customerId", input.customerId);
    }
    if (input.quotationDocumentId && input.quotationDocumentId !== "") {
      formData.set("quotationDocumentId", input.quotationDocumentId);
    }
    if (input.departmentId && input.departmentId !== "") {
      formData.set("departmentId", input.departmentId);
    }
    if (input.employeeId && input.employeeId !== "") {
      formData.set("employeeId", input.employeeId);
    }
    if (input.responsiblePersonId && input.responsiblePersonId !== "") {
      formData.set("responsiblePersonId", input.responsiblePersonId);
    }

    // Handle dates - they should already be ISO strings from the form
    const startDateStr = toISOForAPI(input.startDate) ?? "";
    formData.set("startDate", startDateStr);

    if (input.endDate !== undefined && input.endDate !== null) {
      const endDateStr = toISOForAPI(input.endDate) ?? "";
      // Only set if it's not an empty string
      if (endDateStr !== "") {
        formData.set("endDate", endDateStr);
      }
    }

    // Handle autoRenewal checkbox - zfd.checkbox() expects "on" when checked, or field not present when unchecked
    if (input.autoRenewal) {
      formData.set("autoRenewal", "on");
    }
    // Don't set it at all if false (zfd.checkbox() handles this)
    formData.set("renewalNoticeDays", String(input.renewalNoticeDays ?? 30));
    if (input.notes) formData.set("notes", input.notes);
    if (input.warnings) formData.set("warnings", input.warnings);
    if (input.specialConditions)
      formData.set("specialConditions", input.specialConditions);

    // Add documents
    if (input.documents && input.documents.length > 0) {
      formData.set("documents", JSON.stringify(input.documents));
    }

    const res = await fetch(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      let errorMessage = `Failed to create contract: ${res.statusText}`;
      try {
        const errorData = await res.json();
        console.error("Contract creation error response:", errorData);

        if (errorData && typeof errorData === "object") {
          // Check for validation errors in cause (from zValidator)
          if (errorData.cause && typeof errorData.cause === "object") {
            const cause = errorData.cause as Record<string, unknown>;
            const messages = Object.entries(cause).map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(", ")}`;
              }
              return `${key}: ${String(value)}`;
            });
            errorMessage =
              messages.length > 0 ? messages.join("; ") : errorMessage;
          } else if (typeof errorData.message === "string") {
            errorMessage = errorData.message;
          } else if (typeof errorData.error === "string") {
            errorMessage = errorData.error;
          } else if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors
              .map((e: unknown) =>
                typeof e === "string" ? e : JSON.stringify(e),
              )
              .join(", ");
          } else {
            // Log full error for debugging
            errorMessage = JSON.stringify(errorData);
          }
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch (parseError) {
        // If JSON parsing fails, use the status text
        console.error("Failed to parse error response:", parseError);
        errorMessage = `Failed to create contract: ${res.statusText}`;
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },

  async update(id: string, input: UpdateContractFormDTO) {
    const formData = new FormData();
    if (input.contractNumber !== undefined) {
      formData.set("contractNumber", input.contractNumber ?? "");
    }
    if (input.title) formData.set("title", input.title);
    if (input.typeId) formData.set("typeId", input.typeId);
    if (input.status) formData.set("status", input.status);
    if (input.counterparty) formData.set("counterparty", input.counterparty);
    if (input.counterpartyContact !== undefined) {
      formData.set("counterpartyContact", input.counterpartyContact ?? "");
    }
    if (input.customerId !== undefined) {
      formData.set("customerId", input.customerId ?? "");
    }
    if (input.quotationDocumentId !== undefined) {
      formData.set("quotationDocumentId", input.quotationDocumentId ?? "");
    }
    // Handle nullable fields - send empty string if null/undefined (schema will transform to null)
    if (input.departmentId !== undefined) {
      formData.set("departmentId", input.departmentId ?? "");
    }
    if (input.employeeId !== undefined) {
      formData.set("employeeId", input.employeeId ?? "");
    }
    if (input.responsiblePersonId !== undefined) {
      formData.set("responsiblePersonId", input.responsiblePersonId ?? "");
    }
    if (input.startDate) {
      formData.set("startDate", toISOForAPI(input.startDate) ?? "");
    }
    if (input.endDate) {
      formData.set("endDate", toISOForAPI(input.endDate) ?? "");
    }
    // Handle autoRenewal checkbox - zfd.checkbox() expects "on" when checked, or field not present when unchecked
    if (input.autoRenewal !== undefined) {
      if (input.autoRenewal) {
        formData.set("autoRenewal", "on");
      }
      // Don't set it at all if false (zfd.checkbox() handles this)
    }
    if (input.renewalNoticeDays) {
      formData.set("renewalNoticeDays", String(input.renewalNoticeDays));
    }
    if (input.notes !== undefined) {
      formData.set("notes", input.notes ?? "");
    }
    if (input.warnings !== undefined) {
      formData.set("warnings", input.warnings ?? "");
    }
    if (input.specialConditions !== undefined) {
      formData.set("specialConditions", input.specialConditions ?? "");
    }

    if (input.documents !== undefined) {
      formData.set("documents", JSON.stringify(input.documents));
    }

    const res = await fetch(`${baseUrl}/${id}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      let errorMessage = `Failed to update contract: ${res.statusText}`;
      try {
        const errorData = await res.json();
        console.error("Contract update error response:", errorData);

        if (errorData && typeof errorData === "object") {
          // Check for validation errors in cause (from zValidator)
          if (errorData.cause && typeof errorData.cause === "object") {
            const cause = errorData.cause as Record<string, unknown>;
            const messages = Object.entries(cause).map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(", ")}`;
              }
              return `${key}: ${String(value)}`;
            });
            errorMessage =
              messages.length > 0 ? messages.join("; ") : errorMessage;
          } else if (typeof errorData.message === "string") {
            errorMessage = errorData.message;
          } else if (typeof errorData.error === "string") {
            errorMessage = errorData.error;
          } else if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors
              .map((e: unknown) =>
                typeof e === "string" ? e : JSON.stringify(e),
              )
              .join(", ");
          } else {
            // Log full error for debugging
            errorMessage = JSON.stringify(errorData);
          }
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch (parseError) {
        // If JSON parsing fails, use the status text
        console.error("Failed to parse error response:", parseError);
        errorMessage = `Failed to update contract: ${res.statusText}`;
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },

  async renew(id: string, input: RenewContractFormDTO) {
    const formData = new FormData();
    formData.set(
      "newEndDate",
      input.newEndDate instanceof Date
        ? input.newEndDate.toISOString()
        : input.newEndDate,
    );
    if (input.renewalDocumentId)
      formData.set("renewalDocumentId", input.renewalDocumentId);
    if (input.notes) formData.set("notes", input.notes);

    const res = await fetch(`${baseUrl}/${id}/renew`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      const errorMessage =
        error?.error ||
        error?.message ||
        `Failed to renew contract: ${res.statusText}`;
      throw new Error(errorMessage);
    }
    return res.json();
  },

  async remove(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete contract: ${res.statusText}`,
      );
    }
    return res.json();
  },

  // Contract Type APIs
  async listTypes() {
    const res = await fetch(`${baseUrl}/types`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch contract types: ${res.statusText}`);
    const data = await res.json();
    return data.items as ContractTypeDTO[];
  },

  async createType(input: CreateContractTypeFormDTO) {
    const formData = new FormData();
    formData.set("name", input.name);
    // Always send description, even if empty - zfd handles optional fields
    if (input.description !== undefined && input.description !== null) {
      formData.set("description", input.description);
    }
    formData.set("orderIndex", String(input.orderIndex ?? 0));

    const res = await fetch(`${baseUrl}/types`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      let errorMessage = `Failed to create contract type: ${res.statusText}`;
      try {
        const errorData = await res.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch {
        // If JSON parsing fails, use the status text
        errorMessage = `Failed to create contract type: ${res.statusText}`;
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },

  async updateType(id: string, input: UpdateContractTypeFormDTO) {
    const formData = new FormData();
    formData.set("name", input.name);
    if (
      input.description !== undefined &&
      input.description !== null &&
      input.description.trim() !== ""
    ) {
      formData.set("description", input.description);
    }
    formData.set("orderIndex", String(input.orderIndex ?? 0));
    // Only send isActive if it's provided (optional in schema now)
    if (input.isActive !== undefined) {
      formData.set("isActive", input.isActive ? "true" : "false");
    }

    const res = await fetch(`${baseUrl}/types/${id}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      let errorMessage = `Failed to update contract type: ${res.statusText}`;
      try {
        const errorData = await res.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch {
        // If JSON parsing fails, use the status text
        errorMessage = `Failed to update contract type: ${res.statusText}`;
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },

  async deleteType(id: string) {
    const res = await fetch(`${baseUrl}/types/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      let errorMessage = `Failed to delete contract type: ${res.statusText}`;
      try {
        const errorData = await res.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } catch {
        // If JSON parsing fails, use the status text
        errorMessage = `Failed to delete contract type: ${res.statusText}`;
      }
      throw new Error(errorMessage);
    }
    return res.json();
  },
};
